<template>
  <b-modal modal-class="application-supporting-questions" :id="id" v-model="visible" lazy hide-footer :title="`How well do you know ${contactName}?`" size="lg">
    <p v-if="!formFields">Please let us know how well you know them and add any supporting text to the referral. A recommendation is someone you know well enough to endorse, an introduction is a connection you thought it was worth showing the job to.</p>
    <p v-else>Thank you for helping us grow our team and shape the Celonis culture! Please answer a few quick questions to help us understand your connection to the candidate. Your input will guide us in ensuring each referral gets the attention it deserves and helps us find the best fit for our team!</p>

    <p v-if="!formFields" class="m-0"><strong>How well do you know them?*:</strong></p>
    <div v-if="!formFields" class="application-referral-type mb-3 d-flex flex-row text-center card">
      <div class="flex-fill py-3 border-right" :class="{ selected: answers.type === 'RECOMMEND' }" @click="() => answers.type = 'RECOMMEND'">Recommend 💬</div>
      <div class="flex-fill py-3"  :class="{ selected: answers.type === 'INTRODUCE' }" @click="() => answers.type = 'INTRODUCE'">Introduce 🤝</div>
    </div>

    <advanced-form
      v-if="formFields"
      autocomplete="off"
      :hideFormControl="true"
      :form="formFields"
      v-bind="form"
      v-model="form"
    />

    <p v-if="!formFields" class="m-0"><strong>Additional information:</strong></p>
    <p v-else class="m-0 mb-2" style="font-size: 13px; line-height: 18px"><strong>Please share any further information about your relationship and how they demonstrate the required skills and capabilities for the role?</strong></p>
    <b-textarea class="mb-3" v-model="answers.message"/>

    <p>Thanks for making this referral!</p>
    <b-button @click="updateApplication" variant="primary">Save answers</b-button>
  </b-modal>
</template>

<script>
import UpdateOneJobApplicationMutation from '@/graphql/mutations/JobApplicationUpdateOne.gql';
import { mapRoot } from '@/mixins/root';
import AdvancedForm from '@src/components/AdvancedForm';

export default {
  name: 'ApplicationSupportingQuestions',
  props: {
    id: {
      type: String,
    },
    application: {
      type: Object,
    },
    contact: {
      type: Object,
    },
  },
  components: {
    AdvancedForm
  },
  data() {
    return {
      answers: {
        type: 'RECOMMEND',
        message: null,
      },
      form: {},
      visible: false,
    };
  },
  created() {
    const { type, message } = this.application.userAnswers || {};
    if (type) {
      this.answers.type = type;
    }
    if (message) {
      this.answers.message = message;
    }
  },
  computed: {
    ...mapRoot([ 'company' ]),
    contactName() {
      return this.application.fullName;
    },
    formFields() {
      const { customReferralFields } = this.company || {};
      if (!Array.isArray(customReferralFields) || !customReferralFields.length) {
        return null;
      }
      return customReferralFields.reduce((r, el) => ({
        ...r,
        [el.key]: {
          type: el.type === 'ENUM' ? 'SELECT' : el.type,
          label: el.name,
          required: el.required,
          choices: JSON.parse(el.options).reduce((opts, opt) => ({
            ...opts,
            [opt]: opt,
          }), {}),
        }
      }), {});
    }
  },
  methods: {
    resolveBooleanExpression(key, expression, values) {
      if (key === 'OR') {
        return expression.reduce((bool, subExpression) => {
          const [ initialKey ] = Object.keys(subExpression);
          return bool || this.resolveBooleanExpression(initialKey, subExpression[initialKey], values);
        }, false);
      } else if (key === 'AND') {
        return expression.reduce((bool, subExpression) => {
          const [ initialKey ] = Object.keys(subExpression);
          return bool && this.resolveBooleanExpression(initialKey, subExpression[initialKey], values);
        }, true);
      }
      return values[key] === expression;
    },
    async updateApplication() {
      console.log({ form: this.form });
      try {
        const { id: applicationID } = this.application;
        const { type, message } = this.answers;
        this.$assert(applicationID, new Error('Missing applicationID'));
        this.$assert(type, new Error('Missing type from answer block'));

        const update = {
          userAnswers: {
            type,
            message,
          },
        };

        if (this.formFields) {
          const { sitePreferences } = this.company || {};
          const { referralRecommendationCriteria } = sitePreferences || {};
          update.customReferralFields = this.form;

          if (referralRecommendationCriteria && typeof referralRecommendationCriteria === 'string') {
            const expr = JSON.parse(referralRecommendationCriteria);
            const [ initialKey ] = Object.keys(expr);
            const isRecommendation = this.resolveBooleanExpression(initialKey, expr[initialKey], this.form);
            console.log('IS RECOMMENDED: ', isRecommendation);
            if (isRecommendation) {
              update.userAnswers.type = 'RECOMMEND';
            } else {
              update.userAnswers.type = 'INTRODUCE';
            }
          }
        }

        await this.$apollo.mutate({
          mutation: UpdateOneJobApplicationMutation,
          variables: {
            applicationID,
            update,
          },
        });

        const { job } = this.application || {};
        const { id: jobID } = job || {};
        if (update.userAnswers.type === 'RECOMMEND') {
          await this.$root.captureLog({
            eventName: 'RECOMMENDATION_CREATED',
            application: { id: this.application.id },
            job: { id: jobID },
            user: { self: true }
          });
        }
        await this.$root.captureLog({
          eventName: 'REFERRAL_FORM_SUBMITTED',
          application: { id: this.application.id },
          job: { id: jobID },
          user: { self: true }
        });

        this.$notification({
          title: update.userAnswers.type === 'RECOMMEND' ? 'Recommendation has been successfully submitted.' : 'Introduction has been successfully updated.',
          message: `Your answers for ${this.contactName}'s application were saved successfully`,
          variant: 'success',
        });

        // this.visible = false;
      } catch (err) {
        this.$notifyErr(err, { prepend: 'Failed to save your answers:' });
      }
    },
  },
};
</script>

<style lang="scss">
.application-supporting-questions {
  .application-referral-type {
    user-select: none;

    .selected {
      background: rgba(25, 212, 22, 0.15);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/modals/application-supporting-questions.vue